import { useFormik } from 'formik'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useRecoilState, useRecoilValue } from 'recoil'
import { FE_URL, ID_TUTELA_LEGALE, ID_TUTELA_LEGALE_CIVILE_PENALE } from 'src/config'
import { Paths } from 'src/navigation'
import { usePostPayment, usePostQuestionnaire, usePutPolicy } from 'src/networking/terminals'
import {
  atomDossier,
  atomFormikPolicy,
  atomFormikProductConfiguration,
  atomPaymentData,
  atomPaymentResponse,
  atomPolicy,
  atomPostPolicyResponse,
  atomQuestionnaire,
  atomSedi,
  atomSelectedProduct,
  selectorAddress,
} from 'src/storage'
import { QuestionnaireWrapper, Sede } from 'src/types'
import { FormikPolicy } from 'src/types/formik'

// import { AxeptaWindow } from 'src/types/window'
import { useProgress } from './sections'
import { useValidationSchemas } from './sections/handlers/validation-schemas.hook'

export const usePolicyConfiguration = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { currentStep, setCurrentStep } = useProgress()

  const [formikPolicy, saveFormikPolicy] = useRecoilState(atomFormikPolicy)
  const formikProductConfiguration = useRecoilValue(atomFormikProductConfiguration)

  const sedi = useRecoilValue(atomSedi)
  const dossier = useRecoilValue(atomDossier)
  const selectedProduct = useRecoilValue(atomSelectedProduct)
  const address = useRecoilValue(selectorAddress)
  const postPolicyResponse = useRecoilValue(atomPostPolicyResponse)
  const [paymentResponse, setPaymentResponse] = useRecoilState(atomPaymentResponse)

  const [policy, setPolicy] = useRecoilState(atomPolicy)
  const [paymentData, setPaymentData] = useRecoilState(atomPaymentData)
  const [questionnaire, setQuestionnaire] = useRecoilState(atomQuestionnaire)

  const validationSchemas = useValidationSchemas()

  const [showUnsuitablePurchase, setShowUnsuitablePurchase] = useState(false)
  const [showModalTutelaLegale, setShowModalTutelaLegale] = useState(false)

  const { crossGate: putPolicy } = usePutPolicy()
  const { crossGate: postPayment } = usePostPayment()
  const { crossGate: postQuestionnaire } = usePostQuestionnaire()

  const [selectedSede, setSelectedSede] = useState<Sede>()
  const [paymentNext, setPaymentNext] = useState(false)

  const formattedAddress = `${address?.indirizzo ? `${address.indirizzo} - ` : ''}${
    address?.comune || ''
  }${address?.provincia ? ` (${address.provincia}),` : ''} ${address?.cap || ''}`

  useEffect(() => {
    if (formikProductConfiguration.company !== undefined) {
      const sede = sedi.find((sede) => sede.code.toString() === formikProductConfiguration.company)
      setSelectedSede(sede)
    }

    if (!paymentData && currentStep === 4) {
      setPaymentResponse(undefined)
      if (policy && selectedProduct) {
        onGoBackPressed()
      } else {
        navigate(Paths.Landing)
      }
    }
  }, [])

  /**
   * Event Navigation Payment
   */
  window.addEventListener('message', function (event) {
    if (event.data === 'axepta_SUCCESS_message') {
      setPaymentResponse('OK')
    } else if (event.data === 'axepta_FAILURE_message') {
      setPaymentResponse('KO')
    }
  })

  /**
   * useEffect
   */
  useEffect(() => {
    if (paymentData?.paymentID && paymentResponse) {
      if (paymentResponse === 'OK') {
        setPaymentResponse(undefined)
        // const successUrl = paymentData?.successUrl
        const successUrl = `${FE_URL}${Paths.PaymentSuccess}`
        window.open(successUrl, '_self')
      } else if (paymentResponse === 'KO') {
        setPaymentResponse(undefined)
        // const failureUrl = paymentData?.failureUrl
        const failureUrl = `${FE_URL}${Paths.PaymentFailure}`
        window.open(failureUrl, '_self')
      }
      setPaymentData(undefined)
    }
  }, [paymentData, paymentResponse])

  const formik = useFormik<FormikPolicy>({
    initialValues: formikPolicy,
    validationSchema: validationSchemas[currentStep - 1],
    validateOnMount: true,
    onSubmit: (values, { setTouched }) => {
      setTouched({})
      saveFormikPolicy(values)
      onFormikSubmit()
    },
  })

  useEffect(() => {
    if (paymentNext) {
      setPaymentNext(false)
      postPayment(
        {
          params: {
            idPolizza: postPolicyResponse?.polizza?.code,
          },
          header: {
            dossierId: dossier?.dossierId,
          },
          body: {
            informativa_precontrattuale: formik?.values?.consentInformativeSet,
            tramissione_digitale: formik?.values?.consentDigitalTransmission,
            privacy: formik?.values?.consentPrivacy,
            marketing: formik?.values?.consentMarketing,
            profilazione: formik?.values?.consentProfiling,
            cessione_dati_to_itas: formik?.values?.consentPersonalData,
          },
        },
        {
          onSuccess: (response) => {
            if (response?.paymentID) {
              setPaymentData(response)
              setCurrentStep(currentStep + 1)
            }
          },
        }
      )
    }
  }, [paymentNext, dossier?.dossierId, postPolicyResponse?.polizza?.code, formik?.values])

  useEffect(() => {
    if (!formik.isValid) {
      triggerFormikCheck()
    }
  }, [formik.isValid])

  const onGoBackPressed = () => {
    if (currentStep > 1) {
      if (currentStep === 4) {
        setPaymentData(undefined)
        setPaymentResponse(undefined)
      }
      setCurrentStep(currentStep - 1)
      formik.setErrors({})
    } else {
      navigate(Paths.Landing)
    }
  }

  const triggerFormikCheck = () => {
    if (currentStep === 3 && !formik.isValid) {
      const element = document.getElementById('consent-section')
      const emailElement = document.getElementById('customer-email-field')

      if (emailElement && formik?.errors?.email) {
        const offset = emailElement.getBoundingClientRect().top + window.scrollY - 50
        window.scrollTo({ top: offset, behavior: 'smooth' })
      } else if (element && formik?.errors?.phone) {
        const offset = element.getBoundingClientRect().top + window.scrollY - 250
        window.scrollTo({ top: offset, behavior: 'smooth' })
      } else if (element && !formik?.errors?.email && !formik?.errors?.phone) {
        const offset = element.getBoundingClientRect().top + window.scrollY - 50
        window.scrollTo({ top: offset, behavior: 'smooth' })
      }
    }
  }

  const onFormikSubmit = () => {
    if (currentStep === 3) {
      setPaymentNext(true)
    } else if (currentStep === 2) {
      if (formik.values.questions?.length !== questionnaire?.questionario?.domande.length) return
      if (!questionnaire) return
      const questionnaireCopy = structuredClone(questionnaire) as QuestionnaireWrapper
      formik?.values?.questions?.forEach((selected, index) => {
        questionnaireCopy?.questionario.domande[index].risposte.forEach((risposta) => {
          risposta.selezionata = selected && risposta.idRisposta.toString() === selected.value
        })
      })
      postQuestionnaire(
        {
          uriParams: {
            idPolizza: postPolicyResponse.polizza.code,
          },
          body: questionnaireCopy as QuestionnaireWrapper,
        },
        {
          onSuccess(response) {
            if (response.questionario.isOk) {
              setQuestionnaire(response)
              setCurrentStep(currentStep + 1)
            } else {
              setShowUnsuitablePurchase(true)
            }
          },
        }
      )
    } else if (currentStep === 1) {
      if (
        formik?.values?.warranties.find(
          (warranty) =>
            (warranty?.warrantyCod === ID_TUTELA_LEGALE ||
              warranty?.warrantyCod === ID_TUTELA_LEGALE_CIVILE_PENALE) &&
            warranty?.isSelected
        )
      ) {
        updatePolicy()
      } else {
        setShowModalTutelaLegale(true)
      }
    }
  }

  const updatePolicy = () => {
    putPolicy(
      {
        uriParams: {
          idPolizza: postPolicyResponse.polizza.code,
        },
        body: policy,
      },
      {
        onSuccess(data) {
          setPolicy(data)
          setCurrentStep(currentStep + 1)
        },
      }
    )
  }

  const onPressContinue = () => {
    setShowModalTutelaLegale(false)
    triggerFormikCheck()
    updatePolicy()
  }

  return {
    t,
    formik,
    policy,
    dossier,
    selectedProduct,
    currentStep,
    selectedSede,
    formattedAddress,
    validationSchemas,
    showUnsuitablePurchase,
    onPressContinue,
    triggerFormikCheck,
    onGoBackPressed,
    setShowUnsuitablePurchase,
    setShowModalTutelaLegale,
    showModalTutelaLegale,
  }
}
